import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleCard } from "@/components/article-card";
import { ArrowRight, Loader2, Mail, Send } from "lucide-react";
import { useSuspenseQuery, useMutation } from "@tanstack/react-query";
import { fetchArticles, fetchNovels, fetchCategories, subscribeNewsletter, fetchFeaturedPosts, fetchSettings } from "@/lib/api";
import { ClientOnly, getImageProps, splitSlug } from "@/lib/utils";
import { useState, useEffect, useMemo } from "react";

export const Route = createFileRoute("/")({
  loader: async ({ context: { queryClient } }) => {
    const [settings] = await Promise.all([
      queryClient.ensureQueryData({ queryKey: ["settings"], queryFn: fetchSettings }),
      queryClient.ensureQueryData({ queryKey: ["articles"], queryFn: fetchArticles }),
      queryClient.ensureQueryData({ queryKey: ["novels"], queryFn: fetchNovels }),
      queryClient.ensureQueryData({ queryKey: ["categories"], queryFn: fetchCategories }),
      queryClient.ensureQueryData({ queryKey: ["featured_posts"], queryFn: fetchFeaturedPosts }),
    ]);
    return { settings };
  },
  head: ({ loaderData }) => {
    const data = loaderData as any;
    const settings = data?.settings;
    const siteName = settings?.site_name || "Lihar Aksara";
    const siteDesc = settings?.site_description || "Blog editorial untuk sastra, renungan, esai, dan novel.";
    const canonicalUrl = "https://liharaksara.web.id";

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": siteName,
      "description": siteDesc,
      "url": canonicalUrl,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${canonicalUrl}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    };

    return {
      title: `${siteName} — Ruang Sunyi untuk Membaca`,
      meta: [
        { name: "description", content: siteDesc },
        { property: "og:title", content: `${siteName} — Ruang Sunyi untuk Membaca` },
        { property: "og:description", content: siteDesc },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: siteName },
        { name: "twitter:description", content: siteDesc },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl }
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
      ],
    };
  },
  component: Index,
});

const LITERARY_QUOTES = [
  { text: "Buku adalah pertemuan dua hati yang saling tidak mengenal, namun saling memahami.", author: "Lihar Aksara" },
  { text: "Menulis adalah cara terbaik untuk berbicara tanpa perlu suara.", author: "Anonim" },
  { text: "Sastra adalah hati yang berbicara kepada hati.", author: "Samuel Richardson" },
  { text: "Membaca adalah jendela dunia, namun menulis adalah cara kita membangun dunia itu sendiri.", author: "Lihar Aksara" },
  { text: "Setiap kata yang ditulis adalah jejak abadi dari pikiran yang fana.", author: "Anonim" },
];

function Index() {
  const { data: articlesData } = useSuspenseQuery({ queryKey: ["articles"], queryFn: fetchArticles }) as any;
  const { data: novelsData } = useSuspenseQuery({ queryKey: ["novels"], queryFn: fetchNovels }) as any;
  const { data: settingsData } = useSuspenseQuery({ queryKey: ["settings"], queryFn: fetchSettings }) as any;
  const { data: featuredPostsData } = useSuspenseQuery({
    queryKey: ["featured_posts"],
    queryFn: fetchFeaturedPosts,
  }) as any;

  const articles = useMemo(() => Array.isArray(articlesData) ? articlesData : [], [articlesData]);
  const novels = useMemo(() => Array.isArray(novelsData) ? novelsData : [], [novelsData]);
  const featuredPosts = useMemo(() => Array.isArray(featuredPostsData) ? featuredPostsData : [], [featuredPostsData]);
  const settings = settingsData || {};

  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  
  const hero = featuredPosts[0] || articles[0];

  const { cerpen, puisi, latest } = useMemo(() => {
    const c = articles.filter((a: any) => 
      a.type === "SHORTSTORY" || 
      a.categories?.some((cat: any) => cat.slug === "cerpen")
    ).slice(0, 3);
    
    const p = articles.filter((a: any) => 
      a.type === "POETRY" || 
      a.categories?.some((cat: any) => cat.slug === "puisi")
    ).slice(0, 4);
    
    const l = articles.filter((a: any) => 
      a.id !== hero?.id && 
      !c.find((item: any) => item.id === a.id) && 
      !p.find((item: any) => item.id === a.id)
    ).slice(0, 4);
    
    return { cerpen: c, puisi: p, latest: l };
  }, [articles, hero?.id]);

  const [quote, setQuote] = useState(() => {
    const heroQuote = hero?.author?.quote;
    if (heroQuote) return { text: heroQuote, author: hero.author?.name || "Anonim" };
    if (settings?.site_quote_text) return { text: settings.site_quote_text, author: settings.site_quote_author || "Lihar Aksara" };
    return LITERARY_QUOTES[0];
  });

  useEffect(() => {
    const heroQuote = hero?.author?.quote;
    if (!heroQuote && !settings?.site_quote_text) {
      const randomIndex = Math.floor(Math.random() * LITERARY_QUOTES.length);
      setQuote(LITERARY_QUOTES[randomIndex]);
    }
  }, [hero?.author?.quote, settings?.site_quote_text]);

  const subscribeMutation = useMutation({
    mutationFn: subscribeNewsletter,
    onSuccess: () => {
      setSuccess(true);
      setEmail("");
    },
    onError: (err: any) => {
      alert(err.response?.data?.message || "Terjadi kesalahan saat berlangganan.");
    },
  });

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    subscribeMutation.mutate(email);
  };

  const getPostRoute = (post: any) => {
    if (!post) return "/artikel/$id/$slug";
    if (post.type === "NOVEL") return "/novel/$id/$slug";
    if (post.type === "SHORTSTORY") return "/cerpen/$id/$slug";
    if (post.type === "POETRY") return "/puisi/$id/$slug";
    
    // Fallback to categories if type is generic ARTICLE
    if (post.categories?.some((c: any) => c.slug === "cerpen")) return "/cerpen/$id/$slug";
    if (post.categories?.some((c: any) => c.slug === "puisi")) return "/puisi/$id/$slug";
    
    return "/artikel/$id/$slug";
  };

  if (!hero) {
    return (
      <div className="container-editorial py-24 text-center">
        <h1 className="font-serif text-3xl">Belum ada tulisan yang diterbitkan.</h1>
        <p className="mt-4 text-muted-foreground">Silakan cek kembali nanti atau hubungi admin.</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* HERO SECTION - CINEMATIC */}
      <section className="relative h-[80vh] min-h-[600px] w-full overflow-hidden bg-muted">
        <div className="absolute inset-0 z-0">
          <img
            {...getImageProps(hero.coverImage, true)}
            alt={hero.title}
            className="w-full h-full object-cover animate-in fade-in duration-1000"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent z-10" />
          <div className="absolute inset-0 bg-black/20 z-10" />
        </div>
        
        <div className="container-editorial relative z-20 h-full flex flex-col justify-end pb-20 md:pb-32">
          <Link
            to={getPostRoute(hero)}
            params={splitSlug(hero.slug || "")}
            className="max-w-3xl group"
          >
            <div className="inline-flex items-center gap-3 px-3 py-1 rounded-full bg-accent shadow-lg shadow-accent/20 mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
               <span className="w-1.5 h-1.5 rounded-full bg-accent-foreground animate-pulse" />
               <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-accent-foreground">Pilihan Editor · {hero.categories?.[0]?.name || "Utama"}</span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] font-medium tracking-tight text-foreground drop-shadow-sm group-hover:text-accent transition-colors duration-500 animate-in fade-in slide-in-from-bottom-6 duration-1000">
              {hero.title}
            </h1>
            <p className="mt-8 text-lg md:text-2xl text-muted-foreground font-serif leading-relaxed line-clamp-2 max-w-2xl animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200">
              {hero.excerpt}
            </p>
            <div className="mt-12 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-10 duration-1000 delay-300">
              <div className="px-8 py-4 bg-foreground text-background rounded-full font-bold uppercase tracking-widest text-[10px] group-hover:bg-accent group-hover:text-white transition-all duration-300 flex items-center gap-3">
                <span>Baca Selengkapnya</span>
                <ArrowRight size={16} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* NOVEL TERBARU */}
      <section className="py-24 md:py-32 bg-background">
        <div className="container-editorial">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-accent">Katalog Seri</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight">Novel & Seri Terbaru</h2>
            </div>
            <Link to="/novel" className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors pb-1 border-b border-border hover:border-accent">
              Jelajahi Katalog <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10 md:gap-12">
            {novels.slice(0, 4).map((novel: any) => (
              <Link 
                key={novel.id} 
                to="/novel/$id/$slug" 
                params={splitSlug(novel.slug || "")} 
                className="group block"
              >
                <div className="aspect-[2/3] overflow-hidden rounded-xl bg-muted mb-6 shadow-2xl shadow-black/40 border border-white/5 relative">
                  <img
                    {...getImageProps(novel.coverImage)}
                    alt={novel.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6">
                     <p className="text-white text-xs font-medium uppercase tracking-widest">Lihat Detail</p>
                  </div>
                </div>
                <h3 className="font-serif text-xl md:text-2xl group-hover:text-accent transition-colors leading-tight mb-2">
                  {novel.title}
                </h3>
                <p className="text-muted-foreground text-xs uppercase tracking-widest font-bold">{novel.author?.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* RANDOM QUOTE */}
      <section className="py-24 bg-secondary/20 border-y border-border relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none select-none">
           <div className="font-serif text-[20vw] leading-none absolute -top-20 -left-10 rotate-12">"</div>
           <div className="font-serif text-[20vw] leading-none absolute -bottom-20 -right-10 -rotate-12">"</div>
        </div>
        <div className="container-prose relative z-10 text-center space-y-8">
           <p className="font-serif text-2xl md:text-4xl italic leading-relaxed text-foreground/80">
              "{quote.text}"
           </p>
           <div className="flex items-center justify-center gap-4">
              <span className="w-8 h-px bg-accent/30" />
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-accent">
                {quote.author}
              </p>
              <span className="w-8 h-px bg-accent/30" />
           </div>
        </div>
      </section>

      {/* CERPEN & PUISI */}
      <section className="py-24 md:py-32">
        <div className="container-editorial">
          <div className="grid lg:grid-cols-12 gap-16 md:gap-24">
            {/* CERPEN */}
            <div className="lg:col-span-7 space-y-16">
              <div className="flex items-end justify-between border-b border-border pb-6">
                <div className="space-y-2">
                  <p className="text-xs font-bold uppercase tracking-[0.3em] text-accent">Fiksi Pendek</p>
                  <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight">Cerita Pendek</h2>
                </div>
                <Link to="/kategori/cerpen" className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors">Lihat Semua</Link>
              </div>
              <div className="space-y-12">
                {cerpen.map((a: any) => (
                  <Link 
                    key={a.id} 
                    to={getPostRoute(a)} 
                    params={splitSlug(a.slug || "")} 
                    className="group flex flex-col md:flex-row gap-8 items-start"
                  >
                    <div className="w-full md:w-48 aspect-[4/3] shrink-0 overflow-hidden rounded-xl bg-muted border border-border/50">
                      <img
                        {...getImageProps(a.coverImage)}
                        alt={a.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="space-y-3">
                      <h3 className="font-serif text-2xl md:text-3xl group-hover:text-accent transition-colors leading-tight">
                        {a.title}
                      </h3>
                      <p className="text-muted-foreground text-sm line-clamp-2 font-serif italic">
                        {a.excerpt}
                      </p>
                      <div className="flex items-center gap-3 pt-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{a.author?.name}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* PUISI */}
            <div className="lg:col-span-5 space-y-16">
               <div className="flex items-end justify-between border-b border-border pb-6">
                <div className="space-y-2">
                  <p className="text-xs font-bold uppercase tracking-[0.3em] text-accent">Larik Makna</p>
                  <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight">Puisi</h2>
                </div>
                <Link to="/kategori/puisi" className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors">Lihat Semua</Link>
              </div>
              <div className="grid gap-12">
                 {puisi.map((p: any) => (
                   <Link 
                    key={p.id} 
                    to={getPostRoute(p)} 
                    params={splitSlug(p.slug || "")} 
                    className="group space-y-4"
                   >
                     <div className="space-y-2">
                        <h3 className="font-serif text-2xl group-hover:text-accent transition-colors leading-tight italic">
                          "{p.title}"
                        </h3>
                        <p className="text-muted-foreground text-xs uppercase tracking-widest font-bold">— {p.author?.name}</p>
                     </div>
                     <p className="text-muted-foreground text-sm line-clamp-3 font-serif leading-relaxed opacity-80">
                        {p.excerpt}
                     </p>
                   </Link>
                 ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-24 md:py-32 bg-foreground text-background">
        <div className="container-editorial">
           <div className="max-w-4xl mx-auto text-center space-y-12">
              <div className="space-y-4">
                 <p className="text-xs font-bold uppercase tracking-[0.4em] text-accent-foreground">Warta Aksara</p>
                 <h2 className="font-serif text-4xl md:text-6xl font-medium tracking-tight">Berlangganan kabar sunyi langsung ke email Anda.</h2>
              </div>
              
              {!success ? (
                <form onSubmit={handleSubscribe} className="relative max-w-2xl mx-auto">
                  <input
                    type="email"
                    placeholder="Alamat email Anda..."
                    className="w-full bg-background/5 border-b-2 border-background/40 py-6 px-4 text-xl md:text-2xl font-serif focus:outline-none focus:border-accent transition-colors placeholder:text-background/60"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button 
                    type="submit"
                    disabled={subscribeMutation.isPending}
                    aria-label="Berlangganan" className="absolute right-0 top-1/2 -translate-y-1/2 p-4 text-accent hover:scale-110 transition-transform disabled:opacity-50"
                  >
                    {subscribeMutation.isPending ? <Loader2 className="animate-spin" /> : <Send size={32} />}
                  </button>
                </form>
              ) : (
                <div className="p-12 bg-accent/10 border border-accent/20 rounded-2xl animate-in zoom-in-95 duration-500">
                   <h3 className="font-serif text-3xl text-accent mb-4">Terima kasih telah bergabung.</h3>
                   <p className="text-background/60">Kami telah mengirimkan konfirmasi ke email Anda.</p>
                </div>
              )}
              
              <p className="text-[10px] uppercase tracking-widest text-background/70 font-bold italic">
                Kami membenci spam sebagaimana kami membenci tulisan tanpa jiwa.
              </p>
           </div>
        </div>
      </section>

      {/* LATEST ARTICLES */}
      <section className="py-24 md:py-32">
        <div className="container-editorial">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-accent">Arsip Terkini</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight">Tulisan Terbaru</h2>
            </div>
            <Link to="/artikel" className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors pb-1 border-b border-border hover:border-accent">
              Lihat Semua Tulisan <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
            {latest.map((a: any) => (
              <ArticleCard key={a.id} article={a} size="compact" />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
